import { createClient, isAuthSessionMissingError, SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '../types/database';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://placeholder.supabase.co';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'placeholder-key';

// Handle auth hash before Supabase client initializes
// Store hash and clear it to prevent Supabase auto-detection from hanging
const hashBeforeInit = window.location.hash;
let storedAuthHash: string | null = null;

if (hashBeforeInit && hashBeforeInit.includes('access_token')) {
  storedAuthHash = hashBeforeInit;
  
  // DON'T redirect if we're already on /reset-password - that page handles its own tokens
  const isResetPasswordPage = window.location.pathname === '/reset-password';
  
  if (!isResetPasswordPage) {
    // Clear hash from URL immediately
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
    
    // Redirect to /auth/callback if not already there
    if (window.location.pathname !== '/auth/callback') {
      // Store in sessionStorage so it survives the redirect
      sessionStorage.setItem('supabase_auth_hash', hashBeforeInit);
      window.location.href = '/auth/callback';
    }
  }
  // For /reset-password, leave the hash intact so ResetPassword component can process it
}

// Export stored hash for AuthCallback to use
export const getStoredAuthHash = (): string | null => {
  // First check if we have it in memory (same page load)
  if (storedAuthHash) return storedAuthHash;
  // Then check sessionStorage (after redirect)
  const stored = sessionStorage.getItem('supabase_auth_hash');
  if (stored) {
    sessionStorage.removeItem('supabase_auth_hash');
    return stored;
  }
  return null;
};

// During View as the database already refuses every write, but a blocked update or delete
// just touches no rows and the page reports success. Refuse writes here first, the way
// the database refuses an insert, so every page shows its usual error.
let viewAsReadOnly = false;
export const setViewAsReadOnly = (readOnly: boolean) => {
  viewAsReadOnly = readOnly;
};

const isDataWrite = (url: string, method: string) =>
  !['GET', 'HEAD'].includes(method) &&
  (url.startsWith(`${supabaseUrl}/rest/v1/`) || url.startsWith(`${supabaseUrl}/storage/v1/`));

const readOnlyRefusal = () =>
  new Response(
    JSON.stringify({ code: '42501', message: 'Read-only while viewing as a User', details: null, hint: null }),
    { status: 403, headers: { 'Content-Type': 'application/json' } },
  );

// A 401 from an edge function can mean the session was ended elsewhere (sign-out on another
// device, admin revoke) while this tab still holds its access token. getUser() asks Auth; if the
// session is gone, sign out here only, which sends the user to /login with a message.
const fetchEndingDeadSessions: typeof fetch = async (input, init) => {
  const url = input instanceof Request ? input.url : String(input);
  const method = (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase();
  if (viewAsReadOnly && isDataWrite(url, method)) return readOnlyRefusal();

  const response = await fetch(input, init);
  if (response.status !== 401 || !url.startsWith(`${supabaseUrl}/functions/v1/`)) return response;

  // Set before the check: Login reads it on mount, which can happen as soon as the session is removed.
  sessionStorage.setItem('auth_message', 'Your session ended, please sign in again');
  const { error } = await supabase.auth.getUser();
  if (!isAuthSessionMissingError(error)) {
    sessionStorage.removeItem('auth_message');
    return response;
  }
  await supabase.auth.signOut({ scope: 'local' });
  // Never settle, so the caller doesn't show its own failure toast on the way to /login.
  return new Promise<Response>(() => {});
};

export const supabase: SupabaseClient<Database> = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: false, // Disabled - we handle manually in AuthCallback
  },
  global: { fetch: fetchEndingDeadSessions },
});

// Auth helpers
export const signUpWithEmail = async (
  email: string,
  password: string,
  firstName: string,
  lastName: string,
  website?: string,
) => {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: {
        first_name: firstName,
        last_name: lastName,
        ...(website ? { website } : {}),
      },
    },
  });
  return { data, error };
};

export const signInWithEmail = async (email: string, password: string) => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });
  return { data, error };
};

export const signInWithGoogle = async () => {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: `${window.location.origin}/auth/callback`,
    },
  });
  return { data, error };
};

export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  return { error };
};

export const resetPassword = async (email: string) => {
  const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
    // Redirect directly to change password page (Implicit Flow)
    redirectTo: `${window.location.origin}/reset-password`,
  });
  return { data, error };
};

export const updatePassword = async (newPassword: string) => {
  const { data, error } = await supabase.auth.updateUser({
    password: newPassword,
  });
  return { data, error };
};

/**
 * Accept a workspace invitation by token via Edge Function (bypasses RLS).
 * Adds user as a member, marks invite accepted, and switches their active workspace.
 * Returns the workspace name on success, or null on failure.
 */
export const acceptInviteByToken = async (
  _userId: string
): Promise<{ workspaceName: string } | null> => {
  const token = sessionStorage.getItem('invite_token');
  if (!token) return null;
  sessionStorage.removeItem('invite_token');

  try {
    const { data, error } = await supabase.functions.invoke('accept-invite', {
      body: { token },
    });

    if (error || !data?.success) return null;

    return { workspaceName: data.workspaceName || 'the workspace' };
  } catch {
    return null;
  }
};
